// components/RoutePolygonTable.jsx (atau DetailPolygonList.jsx)
'use client';
import { useState, useMemo, useEffect, useRef } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip } from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import DetailDialog from '../../../components/DetailDialog';
import { baseMaps } from '../../../utils/mapConfig';
import { useLanguage } from '../../../utils/i18n';

// Stores untuk mengambil data koordinat/geom berdasarkan tipe spatial
import { usePoolList } from '../../../stores/poolStore';
import { useTransitPointList } from '../../../stores/transitPointStore';
import { useGeofenceList } from '../../../stores/geofenceStore';

const formatSpatialType = (rawType) => {
  const type = String(rawType).toLowerCase();
  if (type === 'pool') return { label: 'Pool', color: '#1565c0', bg: '#e3f2fd' };
  if (type === 'transit_point' || type === 'customer point') return { label: 'Customer Point', color: '#2e7d32', bg: '#e8f5e9' };
  if (type === 'geofence') return { label: 'Geofence', color: '#7b1fa2', bg: '#f3e5f5' };
  return { label: rawType || 'Unknown', color: '#424242', bg: '#f5f5f5' };
};

const ReadOnlyMapPreview = ({ points, activeFocusId }) => {
  const { t } = useLanguage();
  const mapRef = useRef(null);
  const containerRef = useRef(null);

  const pointsString = useMemo(() => {
    return JSON.stringify(points.map((p) => p.id || p.spatial_id || p.name));
  }, [points]);

  useEffect(() => {
    if (mapRef.current) return;
    if (!containerRef.current) return;

    const map = L.map(containerRef.current, {
      center: [-2.5489, 118.0148],
      zoom: 5,
      zoomControl: true,
      attributionControl: false,
      layers: [baseMaps['OpenStreetMap'] || Object.values(baseMaps)[0]]
    });

    L.control.layers(baseMaps, null, { position: 'topright' }).addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    map.eachLayer((layer) => {
      if (layer instanceof L.Polygon || layer instanceof L.Polyline || layer instanceof L.Marker || layer instanceof L.CircleMarker) {
        map.removeLayer(layer);
      }
    });

    const validPoints = points.filter((p) => p.lat != null || p.latitude != null || p.geom != null);
    if (validPoints.length === 0) return;

    const latLngs = [];

    validPoints.forEach((point, index) => {
      let lat = point.lat ?? point.latitude ?? point.centroid_lat;
      let lon = point.lon ?? point.longitude ?? point.centroid_lon;

      let markerLatLng = lat != null && lon != null ? [Number(lat), Number(lon)] : null;
      const labelName = point.name || point.spatial_name || 'Polygon';
      const labelType = formatSpatialType(point.type || point.spatial_type).label;

      if (point.geom) {
        try {
          const wkt = point.geom;
          const coordsStr = wkt.replace('POLYGON((', '').replace('))', '').replace('POLYGON ((', '');
          const pairs = coordsStr.split(',');
          const polyCoords = pairs.map((pair) => {
            const [lng, ptLat] = pair.trim().split(' ');
            return [Number(ptLat), Number(lng)];
          });

          if (polyCoords.length > 0) {
            const polygon = L.polygon(polyCoords, { color: '#00279c', weight: 2, fillOpacity: 0.2 }).addTo(map);
            if (!markerLatLng) {
              const center = polygon.getBounds().getCenter();
              markerLatLng = [center.lat, center.lng];
            }
          }
        } catch (e) {
          console.warn('Failed to parse WKT for map preview', e);
        }
      }

      if (markerLatLng) {
        point._calculatedCenter = markerLatLng;
        latLngs.push(markerLatLng);

        const markerColor = index === 0 ? '#4CAF50' : index === validPoints.length - 1 ? '#F44336' : '#2196F3';
        const isFocused = (point.id || point.spatial_id) === activeFocusId;

        const numberedIcon = L.divIcon({
          className: 'custom-number-marker',
          html: `
            <div style="
              background-color: ${markerColor}; color: white; width: 24px; height: 24px;
              border-radius: 50%; border: 2px solid white; display: flex; align-items: center;
              justify-content: center; font-weight: bold; font-size: 12px;
              box-shadow: 0 2px 5px rgba(0,0,0,0.3); transition: transform 0.3s ease;
              ${isFocused ? 'transform: scale(1.4); border-color: #FFEB3B; box-shadow: 0 0 12px rgba(255,235,59,0.9); z-index: 1000;' : ''}
            ">
              ${index + 1}
            </div>
          `,
          iconSize: [24, 24],
          iconAnchor: [12, 12]
        });

        L.marker(markerLatLng, { icon: numberedIcon, zIndexOffset: isFocused ? 1000 : 0 })
          .bindPopup(`<b>${t('masterRoute.sequence', 'Sequence')} ${index + 1}</b><br/>${labelName}<br/>(${labelType})`)
          .addTo(map);
      }
    });

    if (latLngs.length > 1) {
      L.polyline(latLngs, { color: '#8bb4f7', weight: 3, dashArray: '8, 8' }).addTo(map);
    }

    if (latLngs.length > 0 && !activeFocusId) {
      const bounds = L.latLngBounds(latLngs);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
  }, [points, pointsString, activeFocusId, t]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !activeFocusId) return;

    const focusPoint = points.find((p) => (p.id || p.spatial_id) === activeFocusId);
    if (focusPoint && focusPoint._calculatedCenter) {
      map.flyTo(focusPoint._calculatedCenter, 16, { animate: true, duration: 1.5 });
    }
  }, [activeFocusId, points]);

  return (
    <Box
      ref={containerRef}
      sx={{ width: '100%', height: '100%', minHeight: 400, borderRadius: 2, border: '1px solid #e0e0e0', zIndex: 0 }}
    />
  );
};

export default function DetailPolygonList({ open, onClose, routeName = '', polygons = [] }) {
  const { t } = useLanguage();
  const [activeFocusId, setActiveFocusId] = useState(null);

  // Ambil data referensi dari master data (untuk patching koordinat geom)
  const { data: rawPools } = usePoolList(0, 1000, 'name', '');
  const { data: rawTransitPoints } = useTransitPointList(0, 1000, 'name', '');
  const { data: rawGeofences } = useGeofenceList(0, 1000, 'name', '');

  useEffect(() => {
    if (open) setActiveFocusId(null);
  }, [open, routeName]);

  // Patching Data API yang miskin geom menjadi utuh
  const enrichedPolygons = useMemo(() => {
    const sorted = [...(polygons || [])].sort((a, b) => {
      const seqA = a.sequence_number ?? a.urutan ?? 0;
      const seqB = b.sequence_number ?? b.urutan ?? 0;
      return seqA - seqB;
    });

    return sorted.map((poly) => {
      const spId = String(poly.spatial_id);
      const spType = String(poly.spatial_type).toLowerCase();
      let matchedData = null;

      if (spType === 'pool' && rawPools) {
        matchedData = rawPools.find((p) => String(p.id) === spId);
      } else if (spType === 'transit_point' && rawTransitPoints) {
        const list = Array.isArray(rawTransitPoints) ? rawTransitPoints : rawTransitPoints?.data || [];
        matchedData = list.find((t) => String(t.id) === spId);
      } else if (spType === 'geofence' && rawGeofences) {
        const list = Array.isArray(rawGeofences) ? rawGeofences : rawGeofences?.data || [];
        matchedData = list.find((g) => String(g.id) === spId);
      }

      if (matchedData) {
        return {
          ...poly,
          geom: matchedData.geom,
          lat: matchedData.centroid_lat ?? matchedData.latitude,
          lon: matchedData.centroid_lon ?? matchedData.longitude
        };
      }
      return poly;
    });
  }, [polygons, rawPools, rawTransitPoints, rawGeofences]);

  return (
    <DetailDialog
      open={open}
      onClose={onClose}
      title={`${t('masterRoute.detailRoutePolygon', 'Route Polygon Detail:')} ${routeName}`}
      fullScreenMode
      fullWidth
    >
      {/* Container utama dibagi dua kiri dan kanan */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, height: 'calc(100vh - 120px)' }}>
        {/* Sisi Kiri: Tabel */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#4b5563', mb: 2 }}>
            {t('masterRoute.passedLocations', 'List of Passed Locations')} ({enrichedPolygons.length})
          </Typography>

          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: '1px solid #e5e7eb', borderRadius: 2, flexGrow: 1, overflowY: 'auto' }}
          >
            <Table stickyHeader size="small">
              <TableHead sx={{ bgcolor: '#f9fafb' }}>
                <TableRow>
                  <TableCell width="60px" align="center" sx={{ fontWeight: 600, color: '#4b5563' }}>
                    {t('masterRoute.sequence', 'Sequence')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#4b5563' }}>{t('masterRoute.locationName', 'Location Name')}</TableCell>
                  <TableCell width="140px" align="center" sx={{ fontWeight: 600, color: '#4b5563' }}>
                    {t('masterRoute.areaType', 'Area Type')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enrichedPolygons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 3, fontStyle: 'italic', color: 'text.secondary' }}>
                      {t('masterRoute.noPolygonData', 'No polygon data for this route.')}
                    </TableCell>
                  </TableRow>
                ) : (
                  enrichedPolygons.map((poly, index) => {
                    const name = poly.spatial_name || poly.name || '-';
                    const typeObj = formatSpatialType(poly.spatial_type || poly.type);
                    const rowId = poly.id || poly.spatial_id;

                    return (
                      <TableRow
                        key={index}
                        onClick={() => setActiveFocusId(rowId)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: activeFocusId === rowId ? 'rgba(72, 93, 154, 0.08)' : 'inherit',
                          transition: 'background-color 0.2s ease',
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          '&:last-child td, &:last-child th': { border: 0 }
                        }}
                      >
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold">
                            {poly.sequence_number ?? poly.urutan ?? index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="500" color={activeFocusId === rowId ? '#1565c0' : '#374151'}>
                            {name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={typeObj.label}
                            size="small"
                            sx={{ bgcolor: typeObj.bg, color: typeObj.color, fontWeight: 'bold', fontSize: '0.7rem' }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Sisi Kanan: Map */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#4b5563', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <MapIcon fontSize="small" color="primary" /> {t('masterRoute.routeMapVisualization', 'Route Map Visualization')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <ReadOnlyMapPreview points={enrichedPolygons} activeFocusId={activeFocusId} />
          </Box>
        </Box>
      </Box>
    </DetailDialog>
  );
}
